import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Tabs,
    Tab,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getDecodedToken } from 'service/service';
import LoaderComponent from 'components/Loader';
import {
    getWorkflowConfigs,
    createApprovalRequest,
    getApprovalRequests,
    approveRequest,
    rejectRequest,
    resubmitRequest,
    cancelRequest,
    getRequestHistory,
    saveRuleConfig,
    updateRuleConfig,
    getRuleConfigs,
    getEwsApproverDetails,
    EwsApproverDetailDto,
    RuleConfigPayload
} from 'service/approvalWorkflow';

import { SectionHeader, StyledTextField, StyledSelect, StyledTextArea } from './SharedComponents';
import ApprovalRuleSection from './ApprovalRuleSection';
import {
    ApprovalFormState,
    ApprovalLevelFormState,
    ApprovalDecision,
    ApprovalLevel,
    SubmissionEntry,
    SavedRule,
    ApprovalAuditLogEntry,
    MappingRow
} from './types';
import {
    ROLE_TITLE_OPTIONS,
    SLA_OPTIONS,
    REVIEWING_AS_OPTIONS,
    initialApprovalForm,
    initialSubmissions,
    initialApprovalAuditLog,
    SAVE_SIMULATED_DELAY_MS,
    ENFORCE_ASSIGNED_REVIEWER
} from './constants';
import {
    getRiskColor,
    validateApprovalForm,
    getApprovalStatusColor,
    isLevelActionable,
    applyApprovalDecision,
    isSlaBreached,
    ensureQueryVariables,
    balanceJsonBrackets
} from './helpers';

interface RuleDetailRowsProps {
    submission?: SubmissionEntry;
}

const RuleDetailRows: React.FC<RuleDetailRowsProps> = ({ submission }) => {
    if (!submission) return null;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#a0aab4', textTransform: 'uppercase', fontWeight: 600 }}>Rule Name</Typography>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>{submission.ruleName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#a0aab4', textTransform: 'uppercase', fontWeight: 600 }}>Risk Level</Typography>
                    <Box sx={{
                        display: 'inline-block',
                        backgroundColor: getRiskColor(submission.riskLevel),
                        color: '#fff',
                        px: 1,
                        py: 0.2,
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        mt: 0.5
                    }}>
                        {submission.riskLevel?.toUpperCase()}
                    </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#a0aab4', textTransform: 'uppercase', fontWeight: 600 }}>Metric / KPI</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#333' }}>{submission.metricKPI}</Typography>
                </Box>
            </Box>
            <Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#a0aab4', textTransform: 'uppercase', fontWeight: 600 }}>Signal</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#333' }}>{submission.signal}</Typography>
            </Box>
            <Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#a0aab4', textTransform: 'uppercase', fontWeight: 600 }}>Domain / Path</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#333' }}>{submission.domainComponentLayer}</Typography>
            </Box>
            <Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#a0aab4', textTransform: 'uppercase', fontWeight: 600 }}>Data Source</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#333' }}>{submission.dataSource}</Typography>
            </Box>
            <Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#a0aab4', textTransform: 'uppercase', fontWeight: 600 }}>Detection Logic</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#333', fontStyle: 'italic' }}>{submission.detectionLogic}</Typography>
            </Box>
            {submission.notes && (
                <Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#a0aab4', textTransform: 'uppercase', fontWeight: 600 }}>Notes</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#555' }}>{submission.notes}</Typography>
                </Box>
            )}
        </Box>
    );
};

interface ApprovalLevelSectionProps {
    title: string;
    subtitle: string;
    level: ApprovalLevelFormState;
    approverOptions: { value: string; label: string; role?: string; sla?: string }[];
    roleOptions?: { value: string; label: string }[];
    slaOptions?: { value: string; label: string }[];
    onChange: (key: keyof ApprovalLevelFormState, value: string) => void;
    errorText?: string;
    disabled?: boolean;
}

const ApprovalLevelSection: React.FC<ApprovalLevelSectionProps> = ({
    title,
    subtitle,
    level,
    approverOptions,
    roleOptions,
    slaOptions,
    onChange,
    errorText,
    disabled = false
}) => {
    const finalApproverOptions = useMemo(() => {
        const base = approverOptions || [];
        if (level.approverEmail && !base.some((o) => o.value.toLowerCase() === level.approverEmail.toLowerCase())) {
            return [{ value: level.approverEmail, label: level.approverEmail }, ...base];
        }
        return base;
    }, [approverOptions, level.approverEmail]);

    const finalRoleOptions = useMemo(() => {
        const base = roleOptions || ROLE_TITLE_OPTIONS;
        if (level.roleTitle && !base.some((o) => o.value === level.roleTitle || o.label === level.roleTitle)) {
            return [{ value: level.roleTitle, label: level.roleTitle }, ...base];
        }
        return base;
    }, [roleOptions, level.roleTitle]);

    const finalSlaOptions = useMemo(() => {
        const base = slaOptions || SLA_OPTIONS;
        if (level.sla && !base.some((o) => o.value === level.sla || o.label === level.sla)) {
            return [{ value: level.sla, label: level.sla }, ...base];
        }
        return base;
    }, [slaOptions, level.sla]);

    return (
        <Box sx={{ flex: '1 1 400px', minWidth: 320 }}>
            <SectionHeader title={title} />
            <Typography sx={{ fontSize: '0.8rem', color: '#a0aab4', fontStyle: 'italic', mb: 2, mt: -1.5 }}>
                {subtitle}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <StyledSelect
                    label="Approver Email / Group"
                    placeholder="Select Approver"
                    value={level.approverEmail}
                    onChange={(val) => onChange('approverEmail', val)}
                    options={finalApproverOptions}
                    disabled={disabled}
                    hideLabel={false}
                    bgColor="#ffffff"
                    height={50}
                    borderRadius={1}
                    error={!!errorText}
                    helperText={errorText}
                />
                <StyledSelect
                    label="Role Title"
                    placeholder="Select Role"
                    value={level.roleTitle}
                    onChange={(val) => onChange('roleTitle', val)}
                    options={finalRoleOptions}
                    disabled={disabled}
                    hideLabel={false}
                    bgColor="#ffffff"
                    height={50}
                    borderRadius={1}
                />
                <StyledSelect
                    label="SLA"
                    value={level.sla}
                    onChange={(val) => onChange('sla', val)}
                    options={finalSlaOptions}
                    disabled={disabled}
                    hideLabel={false}
                    bgColor="#ffffff"
                    height={50}
                    borderRadius={1}
                />
            </Box>
        </Box>
    );
};

const ApprovalLevelStatusBox: React.FC<{ title: string; subtitle: string; status: string; approverEmail: string; sla: string }> = ({
    title, subtitle, status, approverEmail, sla
}) => {
    const statusStyle = getApprovalStatusColor(status);
    return (
        <Box sx={{
            flex: 1,
            backgroundColor: '#fff',
            p: 2,
            borderRadius: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>{title}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#a0aab4', fontStyle: 'italic', mb: 1.5 }}>{subtitle}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>Status</Typography>
                <Box sx={{
                    backgroundColor: statusStyle.bg,
                    color: statusStyle.color,
                    px: 1.5,
                    py: 0.2,
                    borderRadius: '12px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase'
                }}>
                    {status}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>Approver</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#333', fontWeight: 500 }}>{approverEmail || '—'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>SLA</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#333', fontWeight: 500 }}>{sla || '—'}</Typography>
            </Box>
        </Box>
    );
};

interface ApprovalTabProps {
    onStatusChange?: (isApproved: boolean) => void;
    onRequestChanges?: () => void;
    savedRules?: SavedRule[];
    selectedRule?: SavedRule;
    isActive?: boolean;
    selectedRuleId?: number;
    onSelectRuleId?: (ruleId: number) => void;
    onChangeRule?: (ruleId: number) => void;
    onNavigateToTab?: (tabIdx: number) => void;
    mappings?: MappingRow[];
}

const ApprovalTab: React.FC<ApprovalTabProps> = ({
    onStatusChange,
    onRequestChanges,
    savedRules,
    selectedRule,
    isActive,
    selectedRuleId,
    onSelectRuleId,
    onChangeRule,
    onNavigateToTab,
    mappings
}) => {
    const [activeSubTab, setActiveSubTab] = useState(0);
    const [reviewingAsLevel, setReviewingAsLevel] = useState('1');
    const [expandedSubmissionId, setExpandedSubmissionId] = useState<number | null>(null);
    const [reviewerComment, setReviewerComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [decisionLoading, setDecisionLoading] = useState<{ id: number; decision: ApprovalDecision } | null>(null);

    const [form, setForm] = useState<ApprovalFormState>(initialApprovalForm);
    const [approvers, setApprovers] = useState<EwsApproverDetailDto[]>([]);
    const [activeWorkflowConfigId, setActiveWorkflowConfigId] = useState<string | undefined>(undefined);
    const [touched, setTouched] = useState<{ level1?: boolean; level2?: boolean }>({});
    const [submissions, setSubmissions] = useState<SubmissionEntry[]>(initialSubmissions);
    const [auditLog, setAuditLog] = useState<ApprovalAuditLogEntry[]>(initialApprovalAuditLog);
    const [isLoadingApprovalData, setIsLoadingApprovalData] = useState(false);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // Not memoized: getDecodedToken() is a cheap cookie read + JWT decode, and the
    // session cookie may not be present yet at mount. Re-reading it every render
    // avoids permanently freezing currentUser as '' if it was momentarily missing.
    const currentUser = (() => {
        try {
            const token = getDecodedToken();
            return token ? token.email : '';
        } catch {
            return '';
        }
    })();

    const isAssignedReviewerForAny = useMemo(() => {
        if (!ENFORCE_ASSIGNED_REVIEWER) return true;
        if (!currentUser) return false;
        const userEmail = currentUser.toLowerCase().trim();

        const clean = (emailStr?: string) => {
            if (!emailStr) return '';
            const match = emailStr.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
            return (match ? match[1] : emailStr).toLowerCase().trim();
        };

        // Check if user is assigned as approver for any level in any submission
        const hasMatchingSubmission = submissions.some((s) => {
            const l1 = clean(s.level1ApproverEmail);
            const l2 = clean(s.level2ApproverEmail);
            return (l1 && l1 === userEmail) || (l2 && l2 === userEmail);
        });
        if (hasMatchingSubmission) return true;

        // Check configured approvers list or form level emails
        const isInApproversList = approvers.some((a) => clean(a.email) === userEmail);
        const isInForm = clean(form.level1.approverEmail) === userEmail ||
                         clean(form.level2.approverEmail) === userEmail;

        return isInApproversList || isInForm;
    }, [currentUser, submissions, approvers, form.level1.approverEmail, form.level2.approverEmail]);

    // Intelligently auto-select the reviewing level based on current user's actionable pending requests
    useEffect(() => {
        if (!currentUser || submissions.length === 0) return;
        const userEmail = currentUser.toLowerCase().trim();
        const clean = (emailStr?: string) => {
            if (!emailStr) return '';
            const match = emailStr.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
            return (match ? match[1] : emailStr).toLowerCase().trim();
        };
        const hasPendingL1 = submissions.some((s) => isLevelActionable(s, 1) && clean(s.level1ApproverEmail) === userEmail);
        const hasPendingL2 = submissions.some((s) => isLevelActionable(s, 2) && clean(s.level2ApproverEmail) === userEmail);

        if (!hasPendingL1 && hasPendingL2) {
            setReviewingAsLevel('2');
        } else if (hasPendingL1) {
            setReviewingAsLevel('1');
        }
    }, [currentUser, submissions]);

    useEffect(() => {
        if (!isAssignedReviewerForAny && activeSubTab !== 0) {
            setActiveSubTab(0);
        }
    }, [isAssignedReviewerForAny, activeSubTab]);

    useEffect(() => {
        if (!savedRules || savedRules.length === 0) {
            onStatusChange?.(false);
            return;
        }
        const activeRuleNames = new Set(savedRules.map((r) => r.ruleName.toLowerCase()));
        const isApproved = submissions.some((s) => s.overallStatus === 'APPROVED' && activeRuleNames.has(s.ruleName.toLowerCase())) ||
            savedRules.some((r) => r.status === 'APPROVED' || (r as any).Is_Approved === true || (r as any).isApproved === true);
        onStatusChange?.(isApproved);
    }, [submissions, savedRules, onStatusChange]);

    // Load active workflow configs, approver details, and live approval requests from backend
    useEffect(() => {
        let isMounted = true;
        if (isActive === false) return;

        const fetchApprovalData = async () => {
            try {
                // Fetch live approver details from backend API
                try {
                    const approversResp = await getEwsApproverDetails();
                    const list = Array.isArray(approversResp)
                        ? approversResp
                        : Array.isArray(approversResp?.data)
                            ? approversResp.data
                            : Array.isArray(approversResp?.data?.data)
                                ? approversResp.data.data
                                : [];
                    if (isMounted && list.length > 0) {
                        setApprovers(list);
                    }
                } catch (apprErr) {
                    console.log('Backend approvers fetch note:', apprErr);
                }

                let activeCfg: any = null;
                const configResp = await getWorkflowConfigs({ entityType: 'EWS', isActive: true });
                if (configResp?.data && Array.isArray(configResp.data) && configResp.data.length > 0) {
                    activeCfg = configResp.data[0];
                } else {
                    const fallbackConfigResp = await getWorkflowConfigs({ isActive: true });
                    if (fallbackConfigResp?.data && Array.isArray(fallbackConfigResp.data) && fallbackConfigResp.data.length > 0) {
                        activeCfg = fallbackConfigResp.data[0];
                    }
                }

                if (isMounted && activeCfg) {
                    setActiveWorkflowConfigId(activeCfg._id);
                    setForm((prev) => ({
                        ...prev,
                        approvalMode: activeCfg.mode === 'PARALLEL' ? 'parallel' : 'sequential',
                        escalationContact: activeCfg.escalation?.contactEmail || prev.escalationContact
                    }));
                }

                let reqsList: any[] = [];
                const reqsResp = await getApprovalRequests({ entityType: 'EWS' });
                if (reqsResp?.data && Array.isArray(reqsResp.data) && reqsResp.data.length > 0) {
                    reqsList = reqsResp.data;
                } else if (reqsResp?.data?.items && Array.isArray(reqsResp.data.items) && reqsResp.data.items.length > 0) {
                    reqsList = reqsResp.data.items;
                } else if (reqsResp?.items && Array.isArray(reqsResp.items) && reqsResp.items.length > 0) {
                    reqsList = reqsResp.items;
                } else {
                    const fallbackReqsResp = await getApprovalRequests({});
                    if (fallbackReqsResp?.data && Array.isArray(fallbackReqsResp.data) && fallbackReqsResp.data.length > 0) {
                        reqsList = fallbackReqsResp.data;
                    } else if (fallbackReqsResp?.data?.items && Array.isArray(fallbackReqsResp.data.items) && fallbackReqsResp.data.items.length > 0) {
                        reqsList = fallbackReqsResp.data.items;
                    } else if (fallbackReqsResp?.items && Array.isArray(fallbackReqsResp.items) && fallbackReqsResp.items.length > 0) {
                        reqsList = fallbackReqsResp.items;
                    }
                }

                if (isMounted && reqsList.length > 0) {
                    const mapped: SubmissionEntry[] = reqsList.map((r: any, idx: number) => {
                        const rawWfId = r.workflowConfigId || r.Workflow_Id || r.workflowId;
                        const resolvedWfId = rawWfId ? (typeof rawWfId === 'object' ? rawWfId._id || rawWfId.toString() : String(rawWfId)) : undefined;
                        return {
                            id: idx + 10000,
                            backendId: r._id,
                            entityId: r.entityId ? (typeof r.entityId === 'object' ? r.entityId._id || r.entityId.toString() : r.entityId) : undefined,
                            workflowConfigId: resolvedWfId,
                            workflowId: resolvedWfId,
                            requestNumber: r.requestNumber,
                            ruleName: r.entitySnapshot?.ruleName || r.entityName || (r.requestNumber ? `Rule (${r.requestNumber})` : `EWS Rule (${idx + 1})`),
                            riskLevel: r.entitySnapshot?.riskLevel || 'medium',
                            overallStatus: r.overallStatus === 'PENDING' ? 'PENDING_REVIEW' : r.overallStatus,
                            level1Status: r.levels?.[0]?.status || 'PENDING',
                            level2Status: r.levels?.[1]?.status === 'APPROVED' ? 'APPROVED' : (r.levels?.[1]?.status || (r.mode === 'PARALLEL' ? 'PENDING' : (r.levels?.[0]?.status === 'APPROVED' ? 'PENDING' : 'DRAFT'))),
                            level1ApproverEmail: r.levels?.[0]?.email || '',
                            level2ApproverEmail: r.levels?.[1]?.email || '',
                            level1Sla: `${r.levels?.[0]?.slaHours || 24}h`,
                            level2Sla: `${r.levels?.[1]?.slaHours || 48}h`,
                            approvalMode: r.mode || 'SEQUENTIAL',
                            submittedAt: r.submittedAt ? new Date(r.submittedAt).toLocaleString() : new Date().toLocaleString(),
                            signal: r.entitySnapshot?.signal || 'EWS.Volume.Drift',
                            domainComponentLayer: r.entitySnapshot?.domainComponentLayer || 'record count > ingestion metrics > All',
                            domain: 'Volume',
                            component: 'adf',
                            layer: 'bronze',
                            metricKPI: r.entitySnapshot?.metricKPI || 'record count',
                            dataSource: r.entitySnapshot?.dataSource || 'ingestion metrics',
                            detectionLogic: r.entitySnapshot?.detectionLogic || 'Volume deviates > ±X% from 14–30 day trend',
                            handlerAgent: 'Pepwise DB',
                            notes: r.entitySnapshot?.notes || '',
                            createdAt: r.createdAt ? new Date(r.createdAt).toLocaleString() : new Date().toLocaleString()
                        };
                    });
                    // A request can only ever be created for a rule that existed at the
                    // time, so one whose rule name is no longer in the current savedRules
                    // list means that rule has since been deleted - don't surface it here.
                    const activeRuleNames = savedRules && savedRules.length > 0
                        ? new Set(savedRules.map((r) => r.ruleName.toLowerCase()))
                        : null;
                    const visibleSubmissions = activeRuleNames
                        ? mapped.filter((s) => activeRuleNames.has(s.ruleName.toLowerCase()))
                        : mapped;

                    setSubmissions(visibleSubmissions);
                    if (visibleSubmissions[0]?.workflowConfigId && !activeWorkflowConfigId) {
                        setActiveWorkflowConfigId(visibleSubmissions[0].workflowConfigId);
                    }
                    if (visibleSubmissions.length > 0 && expandedSubmissionId === null) {
                        setExpandedSubmissionId(visibleSubmissions[0].id);
                    }

                    if (visibleSubmissions[0]?.backendId) {
                        try {
                            const historyResp = await getRequestHistory(visibleSubmissions[0].backendId);
                            if (isMounted && historyResp?.data?.history && historyResp.data.history.length > 0) {
                                const formatted = historyResp.data.history.map((h: any) => ({
                                    timestamp: h.at ? new Date(h.at).toLocaleString() : new Date().toLocaleString(),
                                    user: h.by || 'Reviewer',
                                    action: `${h.action} (Level ${h.level || 1})`,
                                    comments: h.comments
                                }));
                                setAuditLog(formatted);
                            }
                        } catch (hErr) {
                            console.log('History load note:', hErr);
                        }
                    }
                }
            } catch (err) {
                console.log('Backend approval data fetch note:', err);
            } finally {
                if (isMounted) {
                    setIsLoadingApprovalData(false);
                }
            }
        };

        setIsLoadingApprovalData(true);
        fetchApprovalData();
        return () => { isMounted = false; };
    }, [isActive, savedRules]);

    const activeSavedRule = useMemo(() => {
        if (selectedRule) return selectedRule;
        if (!savedRules || savedRules.length === 0) return undefined;
        return savedRules[savedRules.length - 1];
    }, [selectedRule, savedRules]);

    // Sync saved rules from current session into submissions
    useEffect(() => {
        if (savedRules && savedRules.length > 0) {
            setSubmissions((prev) => {
                const updated = [...prev];
                savedRules.forEach((rule, idx) => {
                    const existingIdx = rule.backendId
                        ? updated.findIndex((s) => s.entityId === rule.backendId || s.backendId === rule.backendId)
                        : -1;
                    const existingEntry = existingIdx >= 0 ? updated[existingIdx] : undefined;
                    const isSequential = form.approvalMode === 'sequential' || form.approvalMode === 'SEQUENTIAL';
                    const entry: SubmissionEntry = {
                        id: rule.id || idx + 1,
                        backendId: existingEntry?.backendId,
                        entityId: rule.backendId,
                        requestNumber: existingEntry?.requestNumber,
                        ruleName: rule.ruleName,
                        version: rule.version || 'v1.0',
                        riskLevel: rule.riskLevel,
                        overallStatus: existingEntry?.overallStatus || (rule.status === 'PENDING_APPROVAL' ? 'PENDING_REVIEW' : (rule.status || 'DRAFT')) as any,
                        level1Status: existingEntry?.level1Status || 'NOT_STARTED',
                        level2Status: existingEntry?.level2Status || (isSequential ? (existingEntry?.level1Status === 'APPROVED' ? 'PENDING' : 'DRAFT') : 'PENDING'),
                        level1ApproverEmail: existingEntry?.level1ApproverEmail || form.level1.approverEmail,
                        level2ApproverEmail: existingEntry?.level2ApproverEmail || form.level2.approverEmail,
                        level1Sla: existingEntry?.level1Sla || form.level1.sla,
                        level2Sla: existingEntry?.level2Sla || form.level2.sla,
                        approvalMode: existingEntry?.approvalMode || form.approvalMode,
                        submittedAt: existingEntry?.submittedAt || new Date().toLocaleString(),
                        signal: rule.ewsSignalName,
                        domainComponentLayer: `${rule.domain || rule.metric || 'Volume'} > ${rule.dataSourceLabel || rule.component || 'Ingestion'} > ${rule.layer || 'Bronze / All'}`,
                        domain: rule.domain || rule.metric || 'Volume',
                        component: rule.component || 'adf',
                        layer: rule.layer || 'bronze',
                        metricKPI: rule.metricKPI,
                        dataSource: rule.dataSourceLabel || rule.dataSourceValue,
                        detectionLogic: rule.detectionLogic,
                        handlerAgent: 'Pepwise DB',
                        notes: rule.notes,
                        createdAt: new Date().toLocaleString()
                    };
                    if (existingIdx >= 0) {
                        updated[existingIdx] = { ...updated[existingIdx], ...entry };
                    } else if (!rule.backendId) {
                        const unlinkedIdx = updated.findIndex((s) => !s.backendId && !s.entityId && s.ruleName.toLowerCase() === rule.ruleName.toLowerCase());
                        if (unlinkedIdx >= 0) {
                            updated[unlinkedIdx] = { ...updated[unlinkedIdx], ...entry };
                        }
                    }
                });
                return updated;
            });
        }
    }, [savedRules, form]);

    const configuredSubmission = useMemo<SubmissionEntry | undefined>(() => {
        if (!activeSavedRule) {
            return undefined;
        }
        // Match in submissions strictly by backend entity ID if the rule has a saved backend request
        if (activeSavedRule.backendId) {
            const foundByEntity = submissions.find((s) => s.entityId === activeSavedRule.backendId || s.backendId === activeSavedRule.backendId);
            if (foundByEntity) return foundByEntity;
        }
        // If not found in submissions, return a synthesized DRAFT entry so form is editable
        return {
            id: activeSavedRule.id || 1,
            backendId: undefined,
            entityId: activeSavedRule.backendId,
            ruleName: activeSavedRule.ruleName,
            version: activeSavedRule.version || 'v1.0',
            riskLevel: activeSavedRule.riskLevel || 'medium',
            overallStatus: 'DRAFT',
            level1Status: 'NOT_STARTED',
            level2Status: 'DRAFT',
            level1ApproverEmail: form.level1.approverEmail,
            level2ApproverEmail: form.level2.approverEmail,
            level1Sla: form.level1.sla,
            level2Sla: form.level2.sla,
            approvalMode: form.approvalMode,
            submittedAt: new Date().toLocaleString(),
            signal: activeSavedRule.ewsSignalName || 'EWS.Volume.Drift',
            domainComponentLayer: `${activeSavedRule.domain || activeSavedRule.metric || 'Volume'} > ${activeSavedRule.dataSourceLabel || activeSavedRule.component || 'Ingestion'} > ${activeSavedRule.layer || 'Bronze / All'}`,
            domain: activeSavedRule.domain || activeSavedRule.metric || 'Volume',
            component: activeSavedRule.component || 'adf',
            layer: activeSavedRule.layer || 'bronze',
            metricKPI: activeSavedRule.metricKPI || 'record count',
            dataSource: activeSavedRule.dataSourceLabel || activeSavedRule.dataSourceValue || 'ingestion metrics',
            detectionLogic: activeSavedRule.detectionLogic || '',
            handlerAgent: activeSavedRule.handler || 'Pepwise DB',
            notes: activeSavedRule.notes || '',
            createdAt: new Date().toLocaleString()
        };
    }, [activeSavedRule, submissions, form]);

    const isApproved = configuredSubmission?.overallStatus === 'APPROVED' || configuredSubmission?.overallStatus === 'ACTIVE';
    const isAwaitingReview = configuredSubmission?.overallStatus === 'PENDING_REVIEW' || configuredSubmission?.overallStatus === 'PENDING' || configuredSubmission?.overallStatus === 'PENDING_APPROVAL';
    const isConfigLocked = isAwaitingReview || isApproved;
    const formErrors = validateApprovalForm(form);

    useEffect(() => {
        onStatusChange?.(isApproved);
    }, [isApproved, onStatusChange]);

    useEffect(() => {
        const hasLevel1Pending = submissions.some((s) => isLevelActionable(s, 1));
        const hasLevel2Pending = submissions.some((s) => isLevelActionable(s, 2));
        if (!hasLevel1Pending && hasLevel2Pending && reviewingAsLevel === '1') {
            setReviewingAsLevel('2');
        }
    }, [submissions, reviewingAsLevel]);

    const logAuditEntry = (action: string, comments?: string) => {
        setAuditLog((prev) => [{ timestamp: new Date().toLocaleString(), user: currentUser || 'rule owner', action, comments }, ...prev]);
    };

    const approverOptions = useMemo(() => {
        return approvers.map((a) => ({
            value: a.email,
            label: a.role ? `${a.email} (${a.role})` : a.email,
            role: a.role,
            sla: String(a.sla || '')
        }));
    }, [approvers]);

    const l1ApproverOptions = useMemo(() => {
        return approvers.map((a) => ({
            value: a.email,
            label: a.role ? `${a.email} (${a.role})` : a.email,
            role: a.role,
            sla: String(a.sla || '')
        }));
    }, [approvers]);

    const l2ApproverOptions = useMemo(() => {
        return approvers.map((a) => ({
            value: a.email,
            label: a.role ? `${a.email} (${a.role})` : a.email,
            role: a.role,
            sla: String(a.sla || '')
        }));
    }, [approvers]);

    const dynamicRoleOptions = useMemo(() => {
        const rolesFromApi = approvers.map((a) => a.role).filter(Boolean);
        const set = new Set([...rolesFromApi, ...ROLE_TITLE_OPTIONS.map((r) => r.label), ...ROLE_TITLE_OPTIONS.map((r) => r.value)]);
        return Array.from(set).map((role) => ({ value: String(role), label: String(role) }));
    }, [approvers]);

    const dynamicSlaOptions = useMemo(() => {
        const slasFromApi = approvers.map((a) => String(a.sla || '')).filter(Boolean);
        const set = new Set([...slasFromApi, ...SLA_OPTIONS.map((s) => s.label), ...SLA_OPTIONS.map((s) => s.value)]);
        return Array.from(set).map((sla) => ({ value: String(sla), label: String(sla) }));
    }, [approvers]);

    const parseSlaHours = (slaStr?: string): number => {
        if (!slaStr) return 24;
        const match = slaStr.match(/\d+/);
        return match ? parseInt(match[0], 10) : 24;
    };

    const handleLevelChange = (level: 'level1' | 'level2', key: keyof ApprovalLevelFormState, value: string) => {
        setForm((prev) => {
            const nextLevel = { ...prev[level], [key]: value };
            if (key === 'approverEmail') {
                const matchedApiApprover = approvers.find((a) => a.email.toLowerCase() === value.toLowerCase());
                if (matchedApiApprover) {
                    if (matchedApiApprover.role) {
                        nextLevel.roleTitle = matchedApiApprover.role;
                    }
                    if (matchedApiApprover.sla) {
                        nextLevel.sla = String(matchedApiApprover.sla);
                    }
                }
            }
            return { ...prev, [level]: nextLevel };
        });
    };

    const handleSubmitForApproval = async () => {
        setTouched({ level1: true, level2: true });
        if (Object.keys(formErrors).length > 0) {
            setSnackbar({ open: true, message: 'Please provide an Approver Email/Group for both levels before submitting.', severity: 'error' });
            return;
        }

        if (!configuredSubmission) {
            setSnackbar({ open: true, message: 'No rule submission found for approval.', severity: 'info' });
            return;
        }

        setIsSubmitting(true);
        try {
            let cfgId = activeWorkflowConfigId;
            const entityType = 'EWS';

            const generateUniqueMongoId = () => {
                const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
                const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
                return `${timestamp}${randomHex}`;
            };

            let resolvedEntityId = configuredSubmission.entityId || configuredSubmission.backendId || activeSavedRule?.backendId;
            if (!resolvedEntityId || !/^[0-9a-fA-F]{24}$/.test(resolvedEntityId)) {
                const rulePayload: RuleConfigPayload = {
                    Domain: configuredSubmission.domain || activeSavedRule?.domain || 'Volume',
                    Component: configuredSubmission.component || activeSavedRule?.component || 'Ingestion',
                    Layer_Tool: configuredSubmission.layer || activeSavedRule?.layer || 'Bronze / All',
                    Metric_KPI: configuredSubmission.metricKPI || activeSavedRule?.metricKPI || 'record count',
                    EWS_Signal_Name: configuredSubmission.signal || activeSavedRule?.ewsSignalName || configuredSubmission.ruleName || 'EWS.Rule',
                    Detection_Logic_Trend_Pattern: configuredSubmission.detectionLogic || activeSavedRule?.detectionLogic || '',
                    Risk_Level: ((configuredSubmission.riskLevel || activeSavedRule?.riskLevel || 'MEDIUM').toUpperCase()) as any,
                    Data_Source: configuredSubmission.dataSource || activeSavedRule?.dataSourceLabel || 'ingestion metrics',
                    Rule_Name: configuredSubmission.ruleName || activeSavedRule?.ruleName || 'EWS Rule',
                    Notes: form.submitterNotes || configuredSubmission.notes || activeSavedRule?.notes || '',
                    Schedule: {
                        Eval_Window: '14d',
                        Interval: '1h',
                        Cooldown: '30m'
                    },
                    Resources: [],
                    Final_ES_Query: (() => {
                        const rawEs = activeSavedRule?.baseEsQuery;
                        let indexName = (rawEs && typeof rawEs === 'object' && rawEs.indexName) ? rawEs.indexName : 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev';
                        indexName = typeof indexName === 'string' ? indexName.replace(/^'|'$/g, '').replace(/^"|"$/g, '').trim() : indexName;
                        let query = (rawEs && typeof rawEs === 'object' && rawEs.query) ? rawEs.query : (typeof rawEs === 'string' ? rawEs : '{"query":{"match_all":{}}}');
                        query = balanceJsonBrackets(query);
                        const vars = (rawEs && typeof rawEs === 'object' && Array.isArray(rawEs.variables)) ? rawEs.variables : [];
                        const keys = (rawEs && typeof rawEs === 'object' && Array.isArray(rawEs.keysToExtract)) ? rawEs.keysToExtract : [];
                        const resolvedVars = ensureQueryVariables(query, vars, activeSavedRule?.variables || []);
                        return {
                            indexName: indexName || 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev',
                            query,
                            Variables: resolvedVars.map((v: any) => ({
                                variableName: v.variableName || v.variableIdentifier || v.key || '',
                                variableIdentifier: v.variableIdentifier || v.variableName || v.key || '',
                                value: v.value,
                                ...(v.operator ? { operator: v.operator } : {})
                            })),
                            keysToExtract: keys
                        };
                    })(),
                    Variables: [],
                    variables: [],
                    Test_Run_Status: (activeSavedRule as any)?.Test_Run_Status || (activeSavedRule as any)?.testRunStatus || 'PASSED'
                };

                try {
                    const createResp = await saveRuleConfig(rulePayload);
                    const createdData = createResp?.data || createResp;
                    if (createdData?._id) {
                        resolvedEntityId = createdData._id;
                        if (activeSavedRule) {
                            activeSavedRule.backendId = createdData._id;
                        }
                    }
                } catch (saveErr: any) {
                    const errMsg = saveErr?.response?.data?.message || saveErr?.message || '';
                    if (errMsg.includes('Duplicate key') || errMsg.includes('EWS_Signal_Name_1') || saveErr?.response?.status === 400) {
                        try {
                            const listResp = await getRuleConfigs();
                            const list = listResp?.data || listResp;
                            if (Array.isArray(list)) {
                                const found = list.find((doc: any) =>
                                    doc.EWS_Signal_Name && doc.EWS_Signal_Name.toLowerCase() === (rulePayload.EWS_Signal_Name || '').toLowerCase()
                                );
                                if (found?._id) {
                                    resolvedEntityId = found._id;
                                    if (activeSavedRule) {
                                        activeSavedRule.backendId = found._id;
                                    }
                                    await updateRuleConfig(found._id, rulePayload);
                                }
                            }
                        } catch (fErr) {
                            console.log('ApprovalTab duplicate key fallback note:', fErr);
                        }
                    } else {
                        console.log('Rule config auto-save before approval submission note:', saveErr);
                    }
                }
            }

            const validMongoEntityId = (resolvedEntityId && /^[0-9a-fA-F]{24}$/.test(resolvedEntityId))
                ? resolvedEntityId
                : generateUniqueMongoId();

            let approvalRequestId: string | undefined = undefined;
            let workflowId: string | undefined = cfgId;
            let requestNumber: string | undefined = undefined;

            const workflowPayload = {
                name: `${configuredSubmission.ruleName || 'EWS'} Approval Workflow`,
                entityType,
                mode: (form.approvalMode === 'parallel' ? 'PARALLEL' : 'SEQUENTIAL') as 'PARALLEL' | 'SEQUENTIAL',
                levels: [
                    {
                        level: 1,
                        email: form.level1.approverEmail?.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/)?.[1]?.trim() || form.level1.approverEmail.trim(),
                        role: form.level1.roleTitle,
                        slaHours: parseSlaHours(form.level1.sla)
                    },
                    {
                        level: 2,
                        email: form.level2.approverEmail?.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/)?.[1]?.trim() || form.level2.approverEmail.trim(),
                        role: form.level2.roleTitle,
                        slaHours: parseSlaHours(form.level2.sla)
                    }
                ],
                escalation: {
                    enabled: !!form.escalationContact,
                    contactEmail: form.escalationContact || 'escalation.lead@pepsico.com',
                    contactRole: 'Escalation Lead',
                    triggerAfterHours: 24
                },
                postApprovalNotification: {
                    enabled: !!form.postApprovalNotify,
                    recipients: form.postApprovalNotify ? [form.postApprovalNotify] : []
                }
            };

            // Single unified approval request dispatch with embedded workflow configuration
            try {
                const reqResp = await createApprovalRequest({
                    name: workflowPayload.name,
                    ...(cfgId ? { workflowConfigId: cfgId } : {}),
                    workflowConfig: workflowPayload,
                    entityType,
                    entityId: validMongoEntityId,
                    entitySnapshot: {
                        ruleName: configuredSubmission.ruleName,
                        riskLevel: configuredSubmission.riskLevel,
                        signal: configuredSubmission.signal,
                        metricKPI: configuredSubmission.metricKPI,
                        dataSource: configuredSubmission.dataSource,
                        detectionLogic: configuredSubmission.detectionLogic,
                        notes: form.submitterNotes || configuredSubmission.notes,
                        domainComponentLayer: configuredSubmission.domainComponentLayer
                    },
                    submittedBy: currentUser || 'svcedappepwise@pepsico.com',
                    mode: form.approvalMode === 'parallel' ? 'PARALLEL' : 'SEQUENTIAL',
                    levels: workflowPayload.levels,
                    escalation: workflowPayload.escalation,
                    postApprovalNotification: workflowPayload.postApprovalNotification
                });

                const createdData = reqResp?.data || reqResp;
                if (createdData?._id) {
                    approvalRequestId = createdData._id;
                    const rawWfId = createdData.workflowConfigId || createdData.Workflow_Id || createdData.workflowId || cfgId;
                    workflowId = rawWfId ? (typeof rawWfId === 'object' ? rawWfId._id || rawWfId.toString() : String(rawWfId)) : undefined;
                    requestNumber = createdData.requestNumber;
                    if (workflowId) {
                        setActiveWorkflowConfigId(workflowId);
                    }
                }
            } catch (beErr: any) {
                console.log('Backend approval request submission note:', beErr);
                if (beErr?.response?.status === 409 || beErr?.statusCode === 409) {
                    const existingReqNum = beErr?.response?.data?.data?.existingRequestNumber || 'existing request';
                    setSnackbar({ open: true, message: `Rule approval request (${existingReqNum}) is already active.`, severity: 'info' });
                    return;
                }
            }

            // Save returned Approval_Id and Workflow_Id to RuleConfig from Frontend
            const targetRuleId = validMongoEntityId || configuredSubmission.entityId || configuredSubmission.backendId || activeSavedRule?.backendId;
            if (targetRuleId && /^[0-9a-fA-F]{24}$/.test(targetRuleId)) {
                try {
                    await updateRuleConfig(targetRuleId, {
                        Approval_Id: approvalRequestId,
                        Workflow_Id: workflowId,
                        Test_Run_Status: (activeSavedRule as any)?.Test_Run_Status || (activeSavedRule as any)?.testRunStatus || 'PASSED'
                    });
                } catch (ruleUpdateErr) {
                    console.log('Rule config update with approval ID note:', ruleUpdateErr);
                }
            }

            const isSequential = form.approvalMode === 'sequential' || form.approvalMode === 'SEQUENTIAL';

            setSubmissions((prev) => {
                const targetId = configuredSubmission.id;
                const targetEntityId = configuredSubmission.entityId;
                const exists = prev.some((s) => (targetEntityId && s.entityId === targetEntityId) || s.id === targetId);

                if (exists) {
                    return prev.map((s) => (
                        ((targetEntityId && s.entityId === targetEntityId) || s.id === targetId)
                            ? {
                                ...s,
                                backendId: approvalRequestId || s.backendId,
                                entityId: validMongoEntityId,
                                workflowConfigId: workflowId || s.workflowConfigId,
                                workflowId: workflowId || s.workflowId,
                                requestNumber: requestNumber || s.requestNumber,
                                overallStatus: 'PENDING_REVIEW',
                                level1Status: 'PENDING',
                                level2Status: isSequential ? 'DRAFT' : 'PENDING',
                                level1ApproverEmail: form.level1.approverEmail,
                                level2ApproverEmail: form.level2.approverEmail,
                                level1Sla: form.level1.sla,
                                level2Sla: form.level2.sla,
                                approvalMode: form.approvalMode
                            }
                            : s
                    ));
                }

                const newEntry: SubmissionEntry = {
                    ...configuredSubmission,
                    backendId: approvalRequestId,
                    entityId: validMongoEntityId,
                    workflowConfigId: workflowId,
                    workflowId: workflowId,
                    requestNumber: requestNumber,
                    overallStatus: 'PENDING_REVIEW',
                    level1Status: 'PENDING',
                    level2Status: isSequential ? 'DRAFT' : 'PENDING',
                    level1ApproverEmail: form.level1.approverEmail,
                    level2ApproverEmail: form.level2.approverEmail,
                    level1Sla: form.level1.sla,
                    level2Sla: form.level2.sla,
                    approvalMode: form.approvalMode,
                    submittedAt: new Date().toLocaleString()
                };
                return [newEntry, ...prev];
            });
            logAuditEntry(`Rule "${configuredSubmission.ruleName}" submitted for approval by ${currentUser || 'rule owner'}. Reviewers notified.`);
            setSnackbar({ open: true, message: 'Rule submitted for approval successfully. Assigned reviewers have been notified.', severity: 'success' });
            if (isAssignedReviewerForAny) {
                setReviewingAsLevel('1');
            }
        } catch (err: any) {
            setSnackbar({ open: true, message: err?.message || 'Failed to submit approval request.', severity: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelRequest = async () => {
        if (!configuredSubmission?.backendId) return;

        try {
            await cancelRequest(configuredSubmission.backendId, {
                cancelledBy: currentUser || 'dataops.admin@pepsico.com',
                comments: 'Cancelled by submitter.'
            });

            setSubmissions((prev) => prev.map((s) => (
                s.backendId === configuredSubmission.backendId ? { ...s, overallStatus: 'CANCELLED' } : s
            )));

            logAuditEntry(`Rule "${configuredSubmission.ruleName}" approval request cancelled by ${currentUser || 'submitter'}.`);
            setSnackbar({ open: true, message: 'Approval request cancelled.', severity: 'info' });
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || err?.message || 'Failed to cancel the approval request.',
                severity: 'error'
            });
        }
    };

    const reviewingLevel = Number(reviewingAsLevel) as ApprovalLevel;
    const reviewingLevelLabel = REVIEWING_AS_OPTIONS.find((o) => o.value === reviewingAsLevel)?.label ?? '';
    const cleanEmail = (emailStr?: string) => {
        if (!emailStr) return '';
        const match = emailStr.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
        return (match ? match[1] : emailStr).toLowerCase().trim();
    };

    const isAssignedReviewer = (s: SubmissionEntry, level: ApprovalLevel) => {
        const assignedEmail = level === 1 ? s.level1ApproverEmail : s.level2ApproverEmail;
        if (!ENFORCE_ASSIGNED_REVIEWER) return true;
        if (!assignedEmail || !currentUser) return false;
        return cleanEmail(assignedEmail) === cleanEmail(currentUser);
    };

    const getActionAuthorization = (sub: SubmissionEntry, level: ApprovalLevel) => {
        const userEmail = cleanEmail(currentUser);
        const l1Approver = cleanEmail(sub.level1ApproverEmail);
        const l2Approver = cleanEmail(sub.level2ApproverEmail);
        const isSequential = sub.approvalMode === 'sequential' || sub.approvalMode === 'SEQUENTIAL';

        const isL1Assigned = !!userEmail && l1Approver === userEmail;
        const isL2Assigned = !!userEmail && l2Approver === userEmail;

        const isL1Approved = sub.level1Status === 'APPROVED';
        const isL2Approved = sub.level2Status === 'APPROVED';
        const isL1Pending = sub.level1Status === 'PENDING' || sub.level1Status === 'DRAFT' || sub.level1Status === 'NOT_STARTED';
        const isL2Pending = sub.level2Status === 'PENDING' || (isSequential && isL1Approved && !isL2Approved && sub.level2Status !== 'REJECTED');

        if (sub.overallStatus === 'REJECTED' || sub.level1Status === 'REJECTED' || sub.level2Status === 'REJECTED') {
            return { canAct: false, reason: 'This approval request has been rejected.' };
        }

        if (sub.overallStatus === 'APPROVED' || (isL1Approved && isL2Approved)) {
            return { canAct: false, reason: 'This approval request is fully approved.' };
        }

        if (level === 1) {
            if (isL1Approved) {
                return { canAct: false, reason: `Level 1 approval has already been completed by ${sub.level1ApproverEmail || 'Domain Owner'}.` };
            }
            if (isL1Assigned && isL1Pending) {
                return { canAct: true, reason: '' };
            }
            if (isL2Assigned) {
                return { canAct: false, reason: `You are assigned as Level 2 Approver. This request is currently awaiting Level 1 approval by ${sub.level1ApproverEmail || 'Level 1 Approver'}. Action will become available once Level 1 is approved.` };
            }
            return { canAct: false, reason: `You are logged in as ${currentUser || 'Current User'}. Only the assigned Level 1 approver (${sub.level1ApproverEmail || 'Level 1 Approver'}) can take action.` };
        }

        if (level === 2) {
            if (isL2Approved) {
                return { canAct: false, reason: 'Level 2 approval has already been completed.' };
            }
            if (isSequential && !isL1Approved) {
                if (isL2Assigned) {
                    return { canAct: false, reason: `This request is currently awaiting Level 1 approval by ${sub.level1ApproverEmail || 'Level 1 Approver'}. Action will be enabled once Level 1 is approved.` };
                }
                return { canAct: false, reason: `Level 2 review is not active. This request is currently awaiting Level 1 approval by ${sub.level1ApproverEmail || 'Level 1 Approver'}.` };
            }
            if (isL2Assigned && isL2Pending) {
                return { canAct: true, reason: '' };
            }
            if (isL1Assigned) {
                return { canAct: false, reason: `You are assigned as Level 1 Approver. Level 2 approval is assigned to ${sub.level2ApproverEmail || 'Level 2 Approver'}.` };
            }
            return { canAct: false, reason: `You are logged in as ${currentUser || 'Current User'}. Only the assigned Level 2 approver (${sub.level2ApproverEmail || 'Level 2 Approver'}) can take action.` };
        }

        return { canAct: false, reason: 'Action not permitted.' };
    };

    const pendingReviews = useMemo(() => {
        return submissions.filter((s) => {
            const isPending = s.overallStatus === 'PENDING_REVIEW' || s.overallStatus === 'PENDING' || s.overallStatus === 'PENDING_APPROVAL';
            return isPending || s.backendId;
        });
    }, [submissions]);

    const handleDecision = async (submission: SubmissionEntry, level: ApprovalLevel, decision: ApprovalDecision) => {
        if (decisionLoading !== null) return;

        const auth = getActionAuthorization(submission, level);
        if (!auth.canAct) {
            setSnackbar({ open: true, message: auth.reason || 'You are not authorized to take action on this approval level.', severity: 'error' });
            return;
        }

        if (decision !== 'approve' && !reviewerComment.trim()) {
            setSnackbar({ open: true, message: 'A comment is required to reject or request changes.', severity: 'error' });
            return;
        }

        setDecisionLoading({ id: submission.id, decision });

        try {
            const backendReqId = submission.backendId;
            const approverEmail = currentUser || (level === 1 ? submission.level1ApproverEmail : submission.level2ApproverEmail) || 'dataops.approver@pepsico.com';

            if (backendReqId) {
                try {
                    if (decision === 'approve') {
                        await approveRequest(backendReqId, {
                            level,
                            approverEmail,
                            comments: reviewerComment.trim() || undefined
                        });
                    } else if (decision === 'reject') {
                        await rejectRequest(backendReqId, {
                            level,
                            approverEmail,
                            comments: reviewerComment.trim()
                        });
                    } else if (decision === 'request-changes') {
                        await rejectRequest(backendReqId, {
                            level,
                            approverEmail,
                            comments: reviewerComment.trim()
                        });
                        await resubmitRequest(backendReqId, {
                            submittedBy: approverEmail,
                            comments: reviewerComment.trim()
                        });
                    }

                    // Sync latest approval requests from backend
                    const reqsResp = await getApprovalRequests({ entityType: 'EWS' });
                    const reqList = reqsResp?.data || reqsResp?.data?.items || reqsResp?.items || [];
                    if (Array.isArray(reqList) && reqList.length > 0) {
                        const mapped: SubmissionEntry[] = reqList.map((r: any, idx: number) => {
                            const rawWfId = r.workflowConfigId || r.Workflow_Id || r.workflowId;
                            const resolvedWfId = rawWfId ? (typeof rawWfId === 'object' ? rawWfId._id || rawWfId.toString() : String(rawWfId)) : undefined;
                            return {
                                id: idx + 10000,
                                backendId: r._id,
                                entityId: r.entityId ? (typeof r.entityId === 'object' ? r.entityId._id || r.entityId.toString() : r.entityId) : undefined,
                                workflowConfigId: resolvedWfId,
                                workflowId: resolvedWfId,
                                requestNumber: r.requestNumber,
                                ruleName: r.entitySnapshot?.ruleName || r.entityName || (r.requestNumber ? `Rule (${r.requestNumber})` : `EWS Rule (${idx + 1})`),
                                riskLevel: r.entitySnapshot?.riskLevel || 'medium',
                                overallStatus: r.overallStatus === 'PENDING' ? 'PENDING_REVIEW' : r.overallStatus,
                                level1Status: r.levels?.[0]?.status || 'PENDING',
                                level2Status: r.levels?.[1]?.status === 'APPROVED' ? 'APPROVED' : (r.levels?.[1]?.status || (r.mode === 'PARALLEL' ? 'PENDING' : (r.levels?.[0]?.status === 'APPROVED' ? 'PENDING' : 'DRAFT'))),
                                level1ApproverEmail: r.levels?.[0]?.email || '',
                                level2ApproverEmail: r.levels?.[1]?.email || '',
                                level1Sla: `${r.levels?.[0]?.slaHours || 24}h`,
                                level2Sla: `${r.levels?.[1]?.slaHours || 48}h`,
                                approvalMode: r.mode || 'SEQUENTIAL',
                                submittedAt: r.submittedAt ? new Date(r.submittedAt).toLocaleString() : new Date().toLocaleString(),
                                signal: r.entitySnapshot?.signal || 'EWS.Volume.Drift',
                                domainComponentLayer: r.entitySnapshot?.domainComponentLayer || 'record count > ingestion metrics > All',
                                domain: 'Volume',
                                component: 'adf',
                                layer: 'bronze',
                                metricKPI: r.entitySnapshot?.metricKPI || 'record count',
                                dataSource: r.entitySnapshot?.dataSource || 'ingestion metrics',
                                detectionLogic: r.entitySnapshot?.detectionLogic || 'Volume deviates > ±X% from 14–30 day trend',
                                handlerAgent: 'Pepwise DB',
                                notes: r.entitySnapshot?.notes || '',
                                createdAt: r.createdAt ? new Date(r.createdAt).toLocaleString() : new Date().toLocaleString()
                            };
                        });
                        const activeRuleNames = savedRules && savedRules.length > 0
                            ? new Set(savedRules.map((r) => r.ruleName.toLowerCase()))
                            : null;
                        setSubmissions(activeRuleNames
                            ? mapped.filter((s) => activeRuleNames.has(s.ruleName.toLowerCase()))
                            : mapped);
                    }
                } catch (err: any) {
                    console.log('Backend decision update note:', err);
                    setSnackbar({
                        open: true,
                        message: err?.response?.data?.message || err?.message || 'Failed to update the approval request. Please try again.',
                        severity: 'error'
                    });
                    return;
                }
            }

            const updated = applyApprovalDecision(submission, level, decision);
            setSubmissions((prev) => prev.map((s) => (s.id === submission.id ? updated : s)));

            // Update RuleConfig directly from Frontend using the RuleConfig ID (entityId)
            const targetRuleConfigId = submission.entityId || submission.backendId || activeSavedRule?.backendId;
            const targetWorkflowId = submission.workflowConfigId || submission.workflowId || activeWorkflowConfigId || (activeSavedRule as any)?.workflowId || null;
            if (targetRuleConfigId && /^[0-9a-fA-F]{24}$/.test(targetRuleConfigId)) {
                try {
                    if (updated.overallStatus === 'APPROVED') {
                        await updateRuleConfig(targetRuleConfigId, {
                            Is_Approved: true,
                            Approval_Id: backendReqId || submission.backendId || null,
                            Workflow_Id: targetWorkflowId,
                            Domain: submission.domain || 'Volume',
                            Component: submission.component || 'Ingestion',
                            Layer_Tool: submission.layer || 'Bronze / All',
                            Metric_KPI: submission.metricKPI || 'record count',
                            EWS_Signal_Name: submission.signal || submission.ruleName || 'EWS Rule',
                            Risk_Level: ((submission.riskLevel || 'MEDIUM').toUpperCase()) as any,
                            Rule_Name: submission.ruleName,
                            Data_Source: submission.dataSource || 'ingestion metrics',
                            Schedule: {
                                Eval_Window: '14d',
                                Interval: '1h',
                                Cooldown: '30m'
                            }
                        });
                    } else if (decision === 'reject') {
                        await updateRuleConfig(targetRuleConfigId, { Is_Approved: false });
                    }
                } catch (rErr: any) {
                    console.log('Rule config status update from FE note:', rErr?.response?.data?.message || rErr?.message || rErr);
                }
            }

            if (level === 1 && decision === 'approve') {
                setReviewingAsLevel('2');
            }

            const actionLabel = decision === 'approve' ? 'approved' : decision === 'reject' ? 'rejected' : 'requested changes on';
            logAuditEntry(`${reviewingLevelLabel} reviewer ${actionLabel} "${submission.ruleName}".`, reviewerComment.trim() || undefined);

            setSnackbar({ open: true, message: `Decision recorded: ${actionLabel}.`, severity: decision === 'approve' ? 'success' : 'error' });
            setReviewerComment('');
            setExpandedSubmissionId(null);

            if (decision === 'request-changes') {
                onRequestChanges?.();
            }
        } finally {
            setDecisionLoading(null);
        }
    };

    useEffect(() => {
        if (expandedSubmissionId != null) {
            const currentSub = submissions.find((s) => s.id === expandedSubmissionId);
            const historyId = currentSub?.backendId || currentSub?.entityId;
            if (historyId) {
                getRequestHistory(historyId)
                    .then((resp) => {
                        if (resp?.data?.history && resp.data.history.length > 0) {
                            const formatted = resp.data.history.map((h: any) => ({
                                timestamp: h.at ? new Date(h.at).toLocaleString() : new Date().toLocaleString(),
                                user: h.by || 'Reviewer',
                                action: `${h.action} (Level ${h.level || 1})`,
                                comments: h.comments
                            }));
                            setAuditLog(formatted);
                        }
                    })
                    .catch((err) => console.log('getRequestHistory note:', err));
            }
        }
    }, [expandedSubmissionId, submissions]);

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoIcon sx={{ color: '#a0aab4', fontSize: '1.2rem', mr: 1 }} />
                <Typography sx={{ fontStyle: 'italic', color: '#a0aab4', fontSize: '0.9rem' }}>
                    This section is to configure approval workflow and process reviews.
                </Typography>
            </Box>

            <Box sx={{
                backgroundColor: '#f4f7fc',
                borderRadius: '8px',
                p: 2.5,
                border: '1px solid #e2e8f0',
                mb: 3
            }}>
                <Tabs
                    value={activeSubTab}
                    onChange={(_e, v) => setActiveSubTab(v)}
                    sx={{
                        minHeight: 40,
                        borderBottom: '1px solid #cbd5e1',
                        mb: 3,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            minHeight: 40,
                            py: 1,
                            px: 2.5,
                            mr: 1,
                            borderRadius: '6px 6px 0 0',
                            backgroundColor: '#39ACE7 !important',
                            color: '#ffffff !important',
                            opacity: 0.85,
                            '&:hover': {
                                opacity: 1,
                                backgroundColor: '#2898cf !important'
                            },
                            '&.Mui-selected': {
                                opacity: 1,
                                backgroundColor: '#39ACE7 !important',
                                color: '#ffffff !important',
                                fontWeight: 700
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#003771',
                            height: '3px'
                        }
                    }}
                >
                    <Tab label="Approval Configuration" />
                    {isAssignedReviewerForAny && <Tab label="Reviewer Portal" />}
                </Tabs>

                {isLoadingApprovalData && (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <LoaderComponent size={40} />
                    </Box>
                )}

                {!isLoadingApprovalData && activeSubTab === 0 && (
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                        {/* Left Column: Multi-Level Configuration */}
                        <Box sx={{ flex: '1 1 65%', minWidth: 0 }}>
                            <ApprovalRuleSection
                                savedRules={savedRules}
                                selectedRule={selectedRule}
                                selectedRuleId={selectedRuleId}
                                onSelectRuleId={onSelectRuleId}
                                onChangeRule={onChangeRule}
                                onNavigateToTab={onNavigateToTab}
                                mappings={mappings}
                            />
                            {isAwaitingReview && (
                                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: '6px', fontWeight: 500 }}>
                                    This rule approval request has been submitted and is currently <strong>Awaiting Review</strong>. Configuration settings are locked until review decisions are finalized.
                                </Alert>
                            )}
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                                <ApprovalLevelSection
                                    title="Level 1: Domain Owner Approval"
                                    subtitle="Primary reviewer for domain alignment"
                                    level={form.level1}
                                    approverOptions={l1ApproverOptions}
                                    roleOptions={dynamicRoleOptions}
                                    slaOptions={dynamicSlaOptions}
                                    onChange={(key, val) => handleLevelChange('level1', key, val)}
                                    errorText={touched.level1 ? formErrors.level1ApproverEmail : undefined}
                                    disabled={isConfigLocked}
                                />
                                <ApprovalLevelSection
                                    title="Level 2: Platform / Security Approval"
                                    subtitle="Secondary reviewer for platform and security standards"
                                    level={form.level2}
                                    approverOptions={l2ApproverOptions}
                                    roleOptions={dynamicRoleOptions}
                                    slaOptions={dynamicSlaOptions}
                                    onChange={(key, val) => handleLevelChange('level2', key, val)}
                                    errorText={touched.level2 ? formErrors.level2ApproverEmail : undefined}
                                    disabled={isConfigLocked}
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <SectionHeader title="Workflow Settings" />
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 1.5 }}>
                                    <StyledTextField
                                        label="Approval Mode"
                                        value="Sequential"
                                        disabled
                                    />
                                    <StyledTextField
                                        label="Escalation Contact"
                                        placeholder="escalation.lead@pepsico.com"
                                        value={form.escalationContact}
                                        onChange={(e) => setForm((p) => ({ ...p, escalationContact: e.target.value }))}
                                        disabled={isConfigLocked}
                                    />
                                    <StyledTextField
                                        label="Post-Approval Notification"
                                        placeholder="ops.channel@pepsico.com"
                                        value={form.postApprovalNotify}
                                        onChange={(e) => setForm((p) => ({ ...p, postApprovalNotify: e.target.value }))}
                                        disabled={isConfigLocked}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <StyledTextArea
                                    label="Submitter Notes"
                                    rows={3}
                                    placeholder="Add context or notes for the review team"
                                    value={form.submitterNotes}
                                    onChange={(e) => setForm((p) => ({ ...p, submitterNotes: e.target.value }))}
                                    disabled={isConfigLocked}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                {isAwaitingReview && (
                                    <Button
                                        variant="outlined"
                                        onClick={handleCancelRequest}
                                        sx={{
                                            color: '#e53935',
                                            borderColor: '#e53935',
                                            fontWeight: 600,
                                            '&:hover': { borderColor: '#e53935', backgroundColor: 'rgba(229, 57, 53, 0.04)' }
                                        }}
                                    >
                                        CANCEL REQUEST
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    onClick={handleSubmitForApproval}
                                    disabled={isSubmitting || isConfigLocked}
                                    startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
                                    sx={{
                                        backgroundColor: isApproved ? '#2e7d32' : isAwaitingReview ? '#ffa726' : '#39ACE7',
                                        color: '#fff',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        '&:hover': { backgroundColor: isApproved ? '#2e7d32' : isAwaitingReview ? '#fb8c00' : '#2c8ebb' },
                                        '&.Mui-disabled': {
                                            backgroundColor: isApproved ? '#2e7d3299' : isAwaitingReview ? '#ffa72699' : '#39ACE799',
                                            color: '#fff'
                                        }
                                    }}
                                >
                                    {isSubmitting ? 'SUBMITTING...' : isApproved ? 'APPROVED' : isAwaitingReview ? 'AWAITING REVIEW' : 'SUBMIT FOR APPROVAL'}
                                </Button>
                            </Box>
                        </Box>

                        {/* Right Column: Rule Details Sidebar */}
                        <Box sx={{
                            flex: '1 1 35%',
                            backgroundColor: '#fff',
                            borderRadius: 1,
                            p: 2.5,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            border: '1px solid #e2e8f0'
                        }}>
                            <SectionHeader title="Rule For Approval" />
                            <Box sx={{ mt: 2 }}>
                                <RuleDetailRows submission={configuredSubmission} />
                            </Box>
                        </Box>
                    </Box>
                )}

                {!isLoadingApprovalData && activeSubTab === 1 && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Box sx={{ minWidth: 280 }}>
                                <StyledSelect
                                    label="Reviewing As"
                                    value={reviewingAsLevel}
                                    onChange={(val) => setReviewingAsLevel(val)}
                                    options={REVIEWING_AS_OPTIONS}
                                />
                            </Box>
                            <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
                                Active User: <strong>{currentUser || 'Current User'}</strong>
                            </Typography>
                        </Box>

                        {pendingReviews.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6, backgroundColor: '#fff', borderRadius: 1 }}>
                                <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: '3rem', mb: 1 }} />
                                <Typography sx={{ fontWeight: 600, color: '#333' }}>No Pending Reviews</Typography>
                                <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
                                    All rule approval requests for {reviewingLevelLabel} are up to date.
                                </Typography>
                            </Box>
                        ) : (
                            pendingReviews.map((sub, idx) => {
                                const isCardOpen = expandedSubmissionId === sub.id || (expandedSubmissionId === null && idx === 0);
                                return (
                                    <Box key={sub.id} sx={{
                                        backgroundColor: '#fff',
                                        borderRadius: 1,
                                        border: '1px solid #e2e8f0',
                                        mb: 2,
                                        overflow: 'hidden'
                                    }}>
                                        <Box
                                            onClick={() => setExpandedSubmissionId(isCardOpen ? -1 : sub.id)}
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s',
                                                '&:hover': { backgroundColor: '#f8fafc' }
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: "#333333" }}>{sub.ruleName}</Typography>
                                                <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>Submitted: {sub.submittedAt}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{
                                                    backgroundColor: getRiskColor(sub.riskLevel),
                                                    color: '#fff',
                                                    px: 1.5,
                                                    py: 0.2,
                                                    borderRadius: '12px',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {sub.riskLevel}
                                                </Box>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedSubmissionId(isCardOpen ? -1 : sub.id);
                                                    }}
                                                >
                                                    {isCardOpen ? <KeyboardArrowUpIcon sx={{ color: '#39ACE7' }} /> : <KeyboardArrowDownIcon sx={{ color: '#39ACE7' }} />}
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        {isCardOpen && (
                                            <Box sx={{ p: 2.5, borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>Rule Summary</Typography>
                                                    <RuleDetailRows submission={sub} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>Test Execution Results</Typography>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        backgroundColor: '#fff',
                                                        borderRadius: 1,
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                        px: 2,
                                                        py: 1.5,
                                                        mb: 1.5
                                                    }}>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>Rule Execution Test</Typography>
                                                            <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>All 10 sample test runs executed within tolerance</Typography>
                                                        </Box>
                                                        <Box sx={{ backgroundColor: '#c8f7c5', color: '#2e7d32', px: 1.5, py: 0.4, borderRadius: '16px', fontSize: '0.7rem', fontWeight: 700 }}>
                                                            PASS
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Box sx={{ mb: 2 }}>
                                                <StyledTextArea
                                                    label="Reviewer Comments"
                                                    rows={2}
                                                    placeholder="Required when rejecting or requesting changes"
                                                    value={reviewerComment}
                                                    onChange={(e) => setReviewerComment(e.target.value)}
                                                    disabled={decisionLoading !== null}
                                                />
                                            </Box>

                                            {(() => {
                                                const actionAuth = getActionAuthorization(sub, reviewingLevel);
                                                const isProcessingAny = decisionLoading !== null;
                                                const isThisSubProcessing = isProcessingAny && decisionLoading.id === sub.id;
                                                const isRejecting = isThisSubProcessing && decisionLoading.decision === 'reject';
                                                const isApproving = isThisSubProcessing && decisionLoading.decision === 'approve';
                                                const isButtonsDisabled = !actionAuth.canAct || isProcessingAny;

                                                return (
                                                    <Box sx={{ mt: 2 }}>
                                                        {!actionAuth.canAct && actionAuth.reason && (
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                backgroundColor: '#f1f5f9',
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: '6px',
                                                                px: 2,
                                                                py: 1,
                                                                mb: 2
                                                            }}>
                                                                <InfoIcon sx={{ fontSize: '1.1rem', color: '#64748b' }} />
                                                                <Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 500 }}>
                                                                    {actionAuth.reason}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                                                            {/* <Button
                                                                variant="outlined"
                                                                disabled={isButtonsDisabled}
                                                                onClick={() => handleDecision(sub, reviewingLevel, 'request-changes')}
                                                                sx={{
                                                                    color: '#f57f17',
                                                                    borderColor: '#f57f17',
                                                                    fontWeight: 600,
                                                                    '&:hover': { borderColor: '#f57f17', backgroundColor: 'rgba(245, 127, 23, 0.04)' },
                                                                    '&.Mui-disabled': {
                                                                        borderColor: '#cbd5e1 !important',
                                                                        color: '#475569 !important',
                                                                        backgroundColor: '#ffffff !important'
                                                                    }
                                                                }}
                                                            >
                                                                REQUEST CHANGES
                                                            </Button> */}
                                                            <Button
                                                                variant="outlined"
                                                                disabled={isButtonsDisabled}
                                                                onClick={() => handleDecision(sub, reviewingLevel, 'reject')}
                                                                startIcon={isRejecting ? <CircularProgress size={16} sx={{ color: '#e53935' }} /> : undefined}
                                                                sx={{
                                                                    minWidth: '110px',
                                                                    color: '#e53935',
                                                                    borderColor: '#e53935',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.875rem',
                                                                    letterSpacing: '0.02em',
                                                                    '&:hover': { borderColor: '#e53935', backgroundColor: 'rgba(229, 57, 53, 0.04)' },
                                                                    '&.Mui-disabled': isRejecting ? {
                                                                        borderColor: '#e53935 !important',
                                                                        color: '#e53935 !important',
                                                                        backgroundColor: 'rgba(229, 57, 53, 0.04) !important',
                                                                        cursor: 'wait !important',
                                                                        pointerEvents: 'auto !important'
                                                                    } : {
                                                                        borderColor: '#cbd5e1 !important',
                                                                        color: '#475569 !important',
                                                                        backgroundColor: '#ffffff !important',
                                                                        cursor: 'not-allowed !important',
                                                                        pointerEvents: 'auto !important',
                                                                        boxShadow: 'none !important'
                                                                    }
                                                                }}
                                                            >
                                                                {isRejecting ? 'REJECTING...' : 'REJECT'}
                                                            </Button>
                                                            <Button
                                                                variant="contained"
                                                                disabled={isButtonsDisabled}
                                                                onClick={() => handleDecision(sub, reviewingLevel, 'approve')}
                                                                startIcon={isApproving ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : undefined}
                                                                sx={{
                                                                    minWidth: '110px',
                                                                    backgroundColor: '#2e7d32',
                                                                    color: '#ffffff',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.875rem',
                                                                    letterSpacing: '0.02em',
                                                                    boxShadow: 'none',
                                                                    '&:hover': { backgroundColor: '#1b5e20', boxShadow: 'none' },
                                                                    '&.Mui-disabled': isApproving ? {
                                                                        backgroundColor: '#2e7d32 !important',
                                                                        color: '#ffffff !important',
                                                                        border: '1px solid #2e7d32 !important',
                                                                        boxShadow: '0 2px 4px rgba(46, 125, 50, 0.2) !important',
                                                                        opacity: 0.9,
                                                                        cursor: 'wait !important',
                                                                        pointerEvents: 'auto !important'
                                                                    } : {
                                                                        backgroundColor: '#e2e8f0 !important',
                                                                        borderColor: '#cbd5e1 !important',
                                                                        border: '1px solid #cbd5e1 !important',
                                                                        color: '#475569 !important',
                                                                        cursor: 'not-allowed !important',
                                                                        pointerEvents: 'auto !important',
                                                                        boxShadow: 'none !important'
                                                                    }
                                                                }}
                                                            >
                                                                {isApproving ? 'APPROVING...' : 'APPROVE'}
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                    )}
                                </Box>
                                );
                            })
                        )}
                    </Box>
                )}
            </Box>

            {/* Approval Status and Audit History */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                <ApprovalLevelStatusBox
                    title="Level 1: Domain Owner"
                    subtitle="Domain alignment & threshold review"
                    status={configuredSubmission?.level1Status || 'PENDING'}
                    approverEmail={configuredSubmission?.level1ApproverEmail || form.level1.approverEmail}
                    sla={configuredSubmission?.level1Sla || form.level1.sla}
                />
                <ApprovalLevelStatusBox
                    title="Level 2: Platform / Security"
                    subtitle="Architecture and security governance"
                    status={
                        configuredSubmission
                            ? (configuredSubmission.level2Status === 'APPROVED'
                                ? 'APPROVED'
                                : (configuredSubmission.level2Status === 'REJECTED'
                                    ? 'REJECTED'
                                    : (configuredSubmission.level1Status === 'APPROVED'
                                        ? 'READY FOR APPROVAL'
                                        : (form.approvalMode === 'parallel' ? (configuredSubmission.level2Status || 'PENDING') : 'DRAFT'))))
                            : (form.approvalMode === 'parallel' ? 'PENDING' : 'DRAFT')
                    }
                    approverEmail={configuredSubmission?.level2ApproverEmail || form.level2.approverEmail}
                    sla={configuredSubmission?.level2Sla || form.level2.sla}
                />
            </Box>

            {auditLog.length > 0 && (
                <Box sx={{ backgroundColor: '#fff', borderRadius: 1, p: 2, border: '1px solid #e2e8f0', mb: 3 }}>
                    <SectionHeader title="Approval Audit History" />
                    <Box sx={{ mt: 1.5 }}>
                        {auditLog.map((entry, idx) => (
                            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f1f5f9', fontSize: '0.8rem' }}>
                                <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{entry.timestamp}</Typography>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#333' }}>{entry.user}</Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: '#003771' }}>{entry.action}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ApprovalTab;
